import { Base64 } from 'js-base64'
import { cloneDeep, merge as lodashMerge, pick as lodashPick } from 'lodash'
import isEqual from 'lodash/isEqual'
import md5 from 'md5'
import queryString from 'query-string'

export const merge = lodashMerge

export const isLocal = () => {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname.includes('192.168')
  return isLocal
}

export const getDomain = () => {
  if (isLocal()) {
    // return 'https://exchange.hxexchge.com'
    return 'https://www.tope.com'
  }

  return window.location.origin
}

/** 创建接口签名（纯函数，不修改入参） */
export const createApiSign = (params: Record<string, any>) => {
  const appKey = params.app_key
  // 复制一份，排除 app_key，并将 undefined/null 归一为空字符串（0/false 保留）
  const signingParams: Record<string, any> = {}
  for (const key in params) {
    if (key === 'app_key') continue
    const value = (params as Record<string, any>)[key]
    signingParams[key] = value || value === 0 || value === false ? value : ''
  }
  let keys = Object.keys(signingParams)
  const length = keys.length
  keys = keys.sort()
  let sign = ''
  for (let i = 0; i < length; i++) {
    if (sign != '') sign = sign + '&'
    sign = sign + keys[i] + '=' + signingParams[keys[i]]
  }
  sign = sign + appKey
  sign = md5(sign).toString()
  return sign
}

/**
 * 将后端返回的数据从下划线转为驼峰
 * @param data  需要转换的数据
 * @returns {Record<string | number, any>}
 */
export const transformApiResponse = <T>(data: T extends Record<string, any>[] ? T : T extends Record<string, any> ? T : never): T => {
  // 如果数据不是对象（包括数组），直接返回原数据
  if (typeof data !== 'object' || data === null) return data as T
  // 判断数据是否为数组
  const isArray = Array.isArray(data)
  const result = isArray ? [] : ({} as Record<string, any>)

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key as PropertyKey)) {
      // 将下划线分隔的属性名转换为驼峰命名法
      const newKey = key.replace(/_(\w)/g, (_, letter) => letter.toUpperCase())
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      result[newKey] = transformApiResponse(data[key])
    }
  }
  return result as T
}

/** 将驼峰参数转为下换线链接的形式 */
export const transformApiParams = <T>(
  data: T extends Record<string, any>[] ? T : T extends Record<string, any> ? T : never,
  ignoreToCamelCaseFields?: string[],
): Record<string | number, any> => {
  if (typeof data !== 'object' || data === null) return data
  const isArray = Array.isArray(data)
  const result = isArray ? [] : {}
  for (const key in data) {
    // 如果 key 本身包含下划线，则不做转换
    const newKey = ignoreToCamelCaseFields?.includes(key) ? key : key.includes('_') ? key : key.replace(/([A-Z])/g, '_$1').toLowerCase()
    // 还要排除是文件
    if (data[key] instanceof File) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      result[newKey] = data[key]
      continue
    }
    if (typeof data[key] === 'object' && data[key] !== null) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      result[newKey] = transformApiParams(data[key])
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      result[newKey] = data[key]
    }
  }
  return result
}

/** 驼峰字符串转为下划线字符串 */
export const camelCaseToSnakeCase = (str: string) => {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase()
}

/** 判断两个对象是否相等，递归字段的值相等才是真的相等 */
export const isObjectEqual = <T extends Record<string, any>>(obj1: T, obj2: T | undefined) => {
  return isEqual(obj1, obj2)
}

export const DefineComponentProps = <T>(props: (string & keyof T)[]) => {
  return props as unknown as any
}

/** 密码加密，用md5 */
export const encryptPassword = (password: string) => {
  return md5(password)
}

/** 安全的将秒转为毫秒，判断位数是否大于10位 */
export const safeSecondToMillisecond = (second: number | undefined | null) => {
  if (second === undefined || second === null) {
    return 0
  }
  if (typeof second !== 'number') {
    return 0
  }
  if (second.toString().length === 10) {
    return second * 1000
  }
  return second
}

/** 安全的将毫秒转为秒 */
export const safeMillisecondToSecond = (millisecond: number | undefined | null) => {
  if (millisecond === undefined || millisecond === null) {
    return 0
  }
  if (typeof millisecond !== 'number') {
    return 0
  }
  if (millisecond.toString().length === 13) {
    return Math.floor(millisecond / 1000)
  }
  return millisecond
}

/** 将一个16进制的色值按照传入的透明度转为rgba颜色 */
export const transformHexToRgba = (hex: string, opacity: number) => {
  return `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}, ${opacity})`
}

/** 将一个16进制的色值提取为 RGB 值（用于 CSS 变量） */
export const hexToRgb = (hex: string) => {
  return `${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}`
}

/** 将字符串的首字母转为大写 */
export const firstCharToUpper = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const getQueryParams = () => {
  const query = queryString.parse(window.location.search)
  return query
}

/** 将下划线转换为驼峰 */
export const toCamelCase = (str: string) => {
  const key = str.replace(/_(\w)/g, (_, letter) => letter.toUpperCase())
  if (str === key) return str
  return key
}

export const deepClone = <T extends Record<string, any>>(obj: T): T => {
  if (typeof obj !== 'object' || obj === null) {
    return obj
  }
  const result = Array.isArray(obj) ? [] : {}

  const keys = Object.keys(obj)
  for (const key of keys) {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      result[key] = deepClone(obj[key])
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      result[key] = obj[key]
    }
  }
  return result as T
}

export const omit = <T extends Record<string, any>>(obj: T, keys: (string & keyof T)[]) => {
  const result = { ...obj }
  for (const key of keys) {
    delete result[key]
  }
  return result
}

export const formatAddress = (address: string) => {
  // 只能输入数字和英文，将非数字和英文的字符替换为空字符串
  return address.replace(/[^0-9a-zA-Z]/g, '')
}

export const getMin = (a: CustomNumber, b: CustomNumber) => {
  if (a.isGreaterThan(b)) return b
  return a
}

export const getMax = (a: CustomNumber, b: CustomNumber) => {
  if (a.isLessThan(b)) return b
  return a
}

export const generateMd5 = (str: string) => {
  // 将str转为bytes
  const bytes = new TextEncoder().encode(str)
  return md5(bytes)
}

export const randomInt = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export const lodashDeepClone = <T>(args: T) => {
  return cloneDeep<T>(args)
}

export const base64Encode = (str: string) => {
  return Base64.encode(str)
}

export const base64Decode = (str: string) => {
  return Base64.decode(str)
}

export const base64EncodeFromUint8Array = (uint8Array: Uint8Array) => {
  return Base64.fromUint8Array(uint8Array)
}

export const base64DecodeToUint8Array = (str: string) => {
  return Base64.toUint8Array(str)
}

/** 判断浏览器是否支持 PassKey, */
export const isSupportPassKey = async () => {
  if (!navigator.credentials) return false
  if (!PublicKeyCredential) return false
  const results = await Promise.all([PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(), PublicKeyCredential.isConditionalMediationAvailable()])
  return results.every((result) => result === true)
}

export const requestIdleCallbackFrame = (cb: () => void) => {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback((deadline) => {
      if (deadline?.timeRemaining && deadline.timeRemaining() > 0) {
        cb()
      } else {
        requestIdleCallbackFrame(cb)
      }
    })
  } else if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(cb)
  } else {
    setTimeout(() => {
      cb()
    }, 16.6)
  }
}

/**
 * 截断字符串：显示前8位，后面显示省略号
 * @param str 原始字符串
 * @param visibleCount 可选，默认显示8位
 * @returns 格式化后的字符串
 */
export function formatId(str: string, visibleCount: number = 8): string {
  if (!str) return ''
  if (str.length <= visibleCount) return str
  return str.slice(0, visibleCount) + '...'
}

// 将项目语言码映射为 Sumsub Web SDK 支持的语言码
export const mapToSumSubLang = (localeRaw: string): string => {
  const locale = (localeRaw || '').toLowerCase()

  const table: Record<string, string> = {
    // 中文
    zh: 'zh',
    'zh-cn': 'zh',
    zh_hans: 'zh',
    'zh-hans': 'zh',
    zh_cn: 'zh',
    zh_tw: 'zh-tw',
    'zh-hant': 'zh-tw',
    'zh-hk': 'zh-tw',

    // 英文
    en: 'en',
    'en-us': 'en',
    'en-gb': 'en',
    'en-au': 'en',
    'en-ca': 'en',

    // 日/韩/俄
    ja: 'ja',
    'ja-jp': 'ja',
    ko: 'ko',
    'ko-kr': 'ko',
    ru: 'ru',
    'ru-ru': 'ru',

    // 西/葡
    es: 'es',
    'es-es': 'es',
    'es-419': 'es',
    pt: 'pt',
    'pt-pt': 'pt',
    'pt-br': 'pt-br',

    // 常见欧洲语言
    fr: 'fr',
    'fr-fr': 'fr',
    de: 'de',
    'de-de': 'de',
    it: 'it',
    'it-it': 'it',
    tr: 'tr',
    'tr-tr': 'tr',
    pl: 'pl',
    nl: 'nl',
    sv: 'sv',
    cs: 'cs',
    da: 'da',
    et: 'et',
    el: 'el',
    ro: 'ro',
    sk: 'sk',
    uk: 'uk',

    // 东南亚
    vi: 'vi',
    'vi-vn': 'vi',
    id: 'id',
    'id-id': 'id',
    ms: 'ms',
    th: 'th',
    lo: 'lo',
    km: 'km',

    // 中东/中亚/南亚
    ar: 'ar',
    'ar-sa': 'ar',
    fa: 'fa',
    hi: 'hi',
    ur: 'ur',
    uz: 'uz',
    kk: 'kk',

    // 其他
    sw: 'sw',
    zu: 'zu',
    no: 'no',
    my: 'my',
    bg: 'bg',
    bn: 'bn',
    ha: 'ha',
    ka: 'ka',
    az: 'az',
    // 菲律宾语：Sumsub 文档使用 fl，这里兼容 tl/fil 输入
    tl: 'fl',
    fil: 'fl',
    fl: 'fl',
  }

  if (table[locale]) return table[locale]
  const base = locale.split('-')[0]
  if (table[base]) return table[base]
  return 'en'
}
export const pick = lodashPick

export const qs = queryString
/**
 * 生成8个随机小写字母字符串
 * @returns 8个随机小写字母组成的字符串
 */
export const random8Lower = (): string => {
  const array = new Uint8Array(8)
  const randomValues = crypto.getRandomValues(array)
  return String.fromCharCode(...Array.from(randomValues).map((n) => (n % 26) + 97))
}
